import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Button } from "@/components/_atoms/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/_atoms/dialog";
import { type LabelAssignmentContext } from "@/domain/label/components/label-assignment-context";
import { LabelGroupList } from "@/domain/label/components/label-group-list";
import { LabelList } from "@/domain/label/components/label-list";
import { useGetLabelGroupListForWorkspace } from "@/domain/label/hooks/api/use-get-label-group-list-for-workspace";
import { useGetLabelListForWorkspace } from "@/domain/label/hooks/api/use-get-label-list-for-workspace";

interface DataRequestLabelDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspaceId: string;
  value: string;
  onChange: (value: string) => void;
}

export const DataRequestLabelDialog: FC<DataRequestLabelDialogProps> = ({
  open,
  onOpenChange,
  workspaceId,
  value,
  onChange,
}) => {
  const { labelList } = useGetLabelListForWorkspace({ workspaceId });
  const { labelGroupList } = useGetLabelGroupListForWorkspace({ workspaceId });

  const [localSelectedIdSet, setLocalSelectedIdSet] = useState<Set<string>>(() => {
    return new Set(value ? value.split(",").filter(Boolean) : []);
  });

  const prevOpenRef = useRef(open);
  useEffect(() => {
    if (open && !prevOpenRef.current) {
      setLocalSelectedIdSet(new Set(value ? value.split(",").filter(Boolean) : []));
    }
    prevOpenRef.current = open;
  }, [open, value]);

  const labelGroupIdMap = useMemo(
    () => new Map(labelList.map((l) => [l.id, l.labelGroupId ?? null])),
    [labelList],
  );

  const localLabelInstanceList = useMemo(() => {
    return Array.from(localSelectedIdSet).map((labelId) => ({
      id: `local-${labelId}`,
      labelId,
      labelGroupId: labelGroupIdMap.get(labelId) ?? null,
    }));
  }, [localSelectedIdSet, labelGroupIdMap]);

  const handleCreateInstance = useCallback(
    (input: { labelId: string }) => {
      setLocalSelectedIdSet((prev) => {
        const next = new Set(prev);
        next.add(input.labelId);
        return next;
      });
    },
    [],
  );

  const handleDeleteInstance = useCallback(
    (input: { id: string }) => {
      const labelId = input.id.replace("local-", "");
      setLocalSelectedIdSet((prev) => {
        const next = new Set(prev);
        next.delete(labelId);
        return next;
      });
    },
    [],
  );

  const labelAssignmentContext = useMemo<LabelAssignmentContext>(
    () => ({
      ownerId: "data-request-local",
      ownerType: "CLIMATE_ACTION",
      workspaceId,
      labelInstanceList: localLabelInstanceList,
      createLabelInstance: handleCreateInstance,
      deleteLabelInstance: handleDeleteInstance,
    }),
    [workspaceId, localLabelInstanceList, handleCreateInstance, handleDeleteInstance],
  );

  const handleDone = useCallback(() => {
    onChange(Array.from(localSelectedIdSet).join(","));
    onOpenChange(false);
  }, [localSelectedIdSet, onChange, onOpenChange]);

  const hasNoContent = useMemo(
    () => labelGroupList.length === 0 && labelList.filter((l) => !l.labelGroupId).length === 0,
    [labelGroupList, labelList],
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="top-[1%] max-h-[80%] max-w-2xl translate-y-[0%] overflow-y-scroll md:top-[10%]"
        onPointerDownOutside={(e) => {
          e.preventDefault();
        }}
        aria-describedby="data-request-label-dialog-description"
      >
        <DialogTitle>Label verwalten</DialogTitle>
        <span id="data-request-label-dialog-description" className="sr-only">
          Label auswählen für die Datenanfrage.
        </span>
        <div className="flex max-h-[70vh] flex-col text-sm">
          <div className="flex min-h-0 flex-col gap-1 overflow-y-auto">
            <LabelGroupList workspaceId={workspaceId} labelAssignmentContext={labelAssignmentContext} />
            <LabelList workspaceId={workspaceId} labelAssignmentContext={labelAssignmentContext} />
            {hasNoContent && (
              <p className="py-4 text-center text-muted-foreground">Keine Label vorhanden.</p>
            )}
          </div>
          <div className="mt-4 flex justify-end">
            <Button size="sm" onClick={handleDone}>
              Fertig
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
